import { useMemo, useState, useEffect } from "react";
import { AlertTriangle, Loader2, CheckCircle, X, Info, Coins } from "lucide-react";
import { CopyButton, FileDropZone } from "../../../components";
import SearchableSelect from "../../../components/SearchableSelect";
import creditAccountsService from "../../../services/creditAccountsService";
import toast from "react-hot-toast";

// ✅ All networks (for non-USDT-TRC20 coins)
const ALL_NETWORK_OPTIONS = [
  // { value: "TRC20", label: "TRC20 (Tron)" },
  { value: "ERC20", label: "ERC20 (Ethereum)" },
  { value: "BEP20", label: "BEP20 (BSC)" },
];

// ✅ TRC20-only (for USDT (TRC20))
const TRC_ONLY_OPTIONS = [{ value: "TRC20", label: "TRC20 (Tron)" }];

const USDTPayment = ({
  paymentMethods,
  formatCurrency,
  total,
  usdtAddress,
  setUsdtAddress,
  usdtNetwork,
  setUsdtNetwork,
  usdtTxId,
  setUsdtTxId,
  accountId,
  amountValue,
  currency, // fiat (e.g. "USD", "MAD")
  onSuccess,
  onClose,
  t = (s) => s,
}) => {
  const crypto = paymentMethods?.crypto ?? { enabled: false, coins: [] };

  // Build coin options dynamically from admin config
  const coins = useMemo(() => {
    const list = Array.isArray(crypto?.coins) ? crypto.coins : [];
    return list
      .filter(Boolean)
      .map((c) => ({
        coin: String(c.coin ?? "").trim(), // e.g., "USDT (TRC20)", "USDT / USDC", "BTC"
        enabled: !!c.enabled,
        network: String(c.network ?? "").trim(), // admin default
        wallet: String(c.wallet ?? "").trim(),
        additionalInfo: String(c.additionalInfo ?? "").trim(),
      }));
  }, [crypto]);

  // Default selected coin = first enabled coin, or USDT (TRC20) if present, or first coin
  const [coin, setCoin] = useState(() => {
    const firstEnabled = coins.find((c) => c.enabled)?.coin;
    if (firstEnabled) return firstEnabled;

    const usdtTrc = coins.find((c) => c.coin === "USDT (TRC20)")?.coin;
    if (usdtTrc) return usdtTrc;

    return coins[0]?.coin || "USDT (TRC20)";
  });

  const isUsdtTrc = coin === "USDT (TRC20)";

  // Active coin config
  const coinConfig = useMemo(() => {
    return (
      coins.find((c) => c.coin === coin) ?? {
        coin,
        enabled: false,
        network: "TRC20",
        wallet: "",
        additionalInfo: "",
      }
    );
  }, [coins, coin]);

  // Prefer admin default network for this coin
  // USDT (TRC20) => TRC20 only
  // other coins => TRC20 / ERC20 / BEP20 allowed
  useEffect(() => {
    const options = isUsdtTrc ? TRC_ONLY_OPTIONS : ALL_NETWORK_OPTIONS;
    const allowedValues = options.map((o) => o.value);
    let target = usdtNetwork;

    if (!target || !allowedValues.includes(target)) {
      const preferred =
        (coinConfig.network && allowedValues.includes(coinConfig.network)
          ? coinConfig.network
          : options[0]?.value) || "TRC20";
      setUsdtNetwork(preferred);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coin, coinConfig.network, isUsdtTrc]);

  // For labels we can use ALL_NETWORK_OPTIONS (TRC20/ETH/BSC)
  const selectedNetworkLabel =
    ALL_NETWORK_OPTIONS.find((n) => n.value === usdtNetwork)?.label ||
    `${usdtNetwork || "TRC20"}`;

  const adminWallet = coinConfig.wallet;
  const adminNetwork = coinConfig.network || "TRC20";
  const coinEnabled = !!(crypto.enabled && coinConfig.enabled && adminWallet);

  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  const [proofFile, setProofFile] = useState(null);

  // ✅ SAFE amount formatting – don't pass "USDT (TRC20)" to Intl
  const isFiatCode = typeof currency === "string" && /^[A-Z]{3}$/.test(currency.trim());
  const formattedFiatAmount =
    typeof formatCurrency === "function" && isFiatCode
      ? formatCurrency(total, currency.trim())
      : null;

  const plainAmount = Number(total || 0).toFixed(2);

  const handleSubmitPayment = async () => {
    if (!usdtAddress) return toast.error(t(`Please enter your ${coin} address`));
    if (!usdtTxId) return toast.error(t("Please enter the transaction ID"));
    if (!proofFile) return toast.error(t("Please upload proof of payment (mobile full screenshot)"));
    if (!coinEnabled) {
      return toast.error(
        t(`${coin} is not available at the moment. Please choose another method or contact support.`)
      );
    }

    setIsProcessing(true);
    setError(null);
    try {
      const paymentData = {
        accountId,
        amount: amountValue,
        total,
        paymentMethod: "crypto",
        currency, // fiat currency code if needed on backend
        cryptoDetails: {
          coin,
          addressFrom: usdtAddress,
          network: usdtNetwork, // 👈 now can be TRC20 / ERC20 / BEP20 for non-USDT-TRC
          transactionId: usdtTxId,
          addressTo: adminWallet,
          proofScreenshot: proofFile, // dataURL from FileDropZone
        },
      };

      const response = await creditAccountsService.processCryptoPayment(paymentData);

      if (response?.data?.statusCode === 200) {
        setIsComplete(true);
        onSuccess && onSuccess();
      } else {
        const msg = response?.data?.message || t("Payment processing failed");
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      console.error("Crypto payment error:", err);
      const msg = err?.response?.data?.message || t("Payment processing failed. Please try again.");
      setError(msg);
      toast.error(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCoinChange = (value) => {
    const selected = coins.find((c) => c.coin === value);
    if (selected && !selected.enabled) {
      toast.error(t(`${value} is not currently enabled by the recipient.`));
      return;
    }
    setCoin(value);
  };

  // ---------- States UI ----------

  if (isProcessing) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"></div>
        </div>
        <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-2">
          {t(`Processing ${coin} Payment`)}
        </h3>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
          {t("Please wait while we verify your transaction...")}
        </p>
      </div>
    );
  }

  if (isComplete) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
          <CheckCircle size={24} className="text-green-500 dark:text-green-400" />
        </div>
        <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-2">
          {t("Payment Submitted Successfully!")}
        </h3>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
          {t(
            `Your ${coin} payment has been submitted. Your account will be credited once the payment is verified.`
          )}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
          <X size={24} className="text-red-500 dark:text-red-400" />
        </div>
        <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-2">
          {t("Payment Failed")}
        </h3>
        <p className="text-xs text-red-500 dark:text-red-400 mb-4">{error}</p>
        <div className="flex flex-col gap-2 justify-center">
          <button
            onClick={() => setError(null)}
            className="px-4 py-2.5 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 text-xs font-medium"
          >
            {t("Try Again")}
          </button>
        </div>
      </div>
    );
  }

  // ---------- Main UI ----------

  const currentNetworkOptions = isUsdtTrc ? TRC_ONLY_OPTIONS : ALL_NETWORK_OPTIONS;

  return (
    <div className="space-y-4">
      <div className="p-4 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-950/50 rounded-xl border border-neutral-200">
        <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-3">
          {t(`${coin} Payment Instructions`)}
        </h3>

        {/* Token selector (dynamic from admin config) */}
        <div className="mb-3">
          <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
            {t("Token")} {t("(Wallet)")}
          </label>
          <SearchableSelect
            options={coins.map((c) => ({
              id: c.coin,
              name: c.enabled ? c.coin : `${c.coin} ${t("(disabled)")}`,
              value: c.coin,
            }))}
            value={coin}
            onChange={handleCoinChange}
            placeholder={t("Select token")}
            icon={<Coins size={16} />}
          />
        </div>

        {/* Network selector – TRC20 only for USDT (TRC20), all networks for others */}
        <div>
          <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
            {t("Network")}
          </label>
          <SearchableSelect
            options={currentNetworkOptions.map((n) => ({
              id: n.value,
              name: n.label,
              value: n.value,
            }))}
            value={usdtNetwork}
            onChange={(v) => setUsdtNetwork(isUsdtTrc ? "TRC20" : v)}
            placeholder={t("Select network")}
            icon={<Coins size={16} />}
          />
          <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
            {isUsdtTrc
              ? t("USDT (TRC20) can only be sent on the TRC20 (Tron) network.")
              : t("Select the exact network you used to send the payment (ERC20 / BEP20).")}
          </p>
        </div>

        {/* Amount text */}
        <p className="text-xs text-neutral-600 dark:text-neutral-400 mb-3">
          {t("Please send")}{" "}
          {formattedFiatAmount
            ? `${formattedFiatAmount} (${plainAmount} ${coin})`
            : `${plainAmount} ${coin}`}{" "}
          {t("to the following")} {coin} {t("address")}:
        </p>

        <div className="p-3 bg-neutral-100 dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-600 dark:text-neutral-400 rounded-xl font-mono text-xs break-all mb-3 flex justify-between items-center">
          <span>{adminWallet || t("Not available")}</span>
          <CopyButton text={adminWallet} label={`Copy ${coin} address`} />
        </div>

        <div className="space-y-2 mb-2 text-xs">
          <div className="flex justify-between items-center">
            <span className="text-neutral-600 dark:text-neutral-400">{t("Token")}:</span>
            <span className="text-neutral-900 dark:text-neutral-200 font-medium">{coin}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-neutral-600 dark:text-neutral-400">{t("Amount")}:</span>
            <span className="text-neutral-900 dark:text-neutral-200 font-medium">
              {plainAmount} {coin}
            </span>
          </div>
        </div>

        {!!coinConfig.additionalInfo && (
          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-xs text-blue-700 dark:text-blue-300 mt-2 flex items-start gap-2">
            <Info size={12} className="mt-[2px]" />
            <span>{coinConfig.additionalInfo}</span>
          </div>
        )}

        <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded-xl text-xs text-amber-800 dark:text-amber-300 mt-3">
          <AlertTriangle size={12} className="inline-block mr-1" />
          {t(
            `Important: Only send ${coin} on the ${selectedNetworkLabel.split(" ")[0]} network you select above. Other networks may result in lost funds.`
          )}
        </div>

        {!coinEnabled && (
          <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded-xl text-xs text-red-600 dark:text-red-400">
            {t(
              `${coin} is not currently enabled by the recipient or has no wallet set. Please choose another coin or method.`
            )}
          </div>
        )}
      </div>

      {/* User inputs */}
      <div className="space-y-3">
        <div>
          <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
            {t(`Your ${coin} Address`)}
          </label>
          <input
            type="text"
            className="w-full px-3 py-2.5 border border-neutral-300 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-950 text-neutral-800 dark:text-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            placeholder={t("Enter the address you sent from")}
            value={usdtAddress}
            onChange={(e) => setUsdtAddress(e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
            {t("Transaction Link")}
          </label>
          <input
            type="text"
            className="w-full px-3 py-2.5 border border-neutral-300 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-950 text-neutral-800 dark:text-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            placeholder={t("Paste the blockchain explorer link (e.g. tronscan.org/#/transaction/...)")}
            value={usdtTxId}
            onChange={(e) => setUsdtTxId(e.target.value)}
          />
          {usdtTxId && (
            <a
              href={usdtTxId.startsWith("http") ? usdtTxId : `https://${usdtTxId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-1.5 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-2 transition-colors"
            >
              {t("Open transaction")} ↗
            </a>
          )}
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            {t("Paste the full explorer URL of your transaction after sending")}
          </p>
        </div>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
          <div>
            <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
              {t("Proof of Payment")}
            </label>
            <FileDropZone
              file={proofFile}
              setFile={setProofFile}
              allowedTypes={["image/jpeg", "image/png", "image/webp", "image/*"]}
              maxSizeMB={20}
              labelText={t("Drop your screenshot here")}
              hintText={t("or click to browse (images only)")}
            />
            <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
              {t("A full screenshot from a mobile phone is required.")}
            </p>
          </div>
        </div>

        <button
          onClick={handleSubmitPayment}
          disabled={!usdtAddress || !usdtTxId || !coinEnabled || isProcessing || !proofFile}
          className="w-full mt-4 px-4 py-2.5 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isProcessing ? (
            <>
              <Loader2 size={14} className="animate-spin mr-2" />
              {t("Processing...")}
            </>
          ) : (
            t(`Submit ${coin} Payment`)
          )}
        </button>
      </div>
    </div>
  );
};

export default USDTPayment;
